/**
 * El estado del alta, leído de `GET /job-apply/api/onboarding-status`.
 *
 * Vive fuera de la pantalla a propósito: el shell también necesita saber si
 * alguien todavía está en el alta para decidir qué barra dibujar, y ese cálculo
 * no puede estar escrito dos veces con dos criterios distintos.
 *
 * ── LAS DOS BANDERAS, que hablan de cosas diferentes ──────────────────────
 *
 * `domain_channel_available`  hay dominios vivos: se puede dar de alta a alguien
 *                             en el canal gestionado ahora mismo.
 * `gmail_signup_enabled`      el alta por Gmail se puede OFRECER. Mientras
 *                             Google tenga la verificación en evaluación
 *                             (CASA), viene en false y el CTA no se pinta.
 *
 * La segunda habla de ALTAS, no de esta persona: quien ya tiene Gmail conectado
 * sigue enviando y sigue pudiendo reconectar con la bandera en false. Por eso
 * `isGmailPathOffered()` sólo se consulta para ofrecer el camino a alguien
 * nuevo, y nunca para esconder una reconexión.
 */

import { currentViewAs } from '~/lib/viewAs';
import type { EuWorkAuthorization, H2Status } from './immigration';

export interface OnboardingStatus {
  has_membership: boolean;
  has_gmail: boolean;
  /** True con Gmail conectado O con el envío gestionado activo. */
  has_email_channel?: boolean;
  send_channel?: 'gmail' | 'domain';
  /** False mientras no haya dominio vivo: el CTA gestionado sólo daría 503. */
  domain_channel_available?: boolean;
  /** False mientras Google tenga la verificación pendiente. Habla de altas. */
  gmail_signup_enabled?: boolean;
  sender_slug?: string | null;
  sender_domain?: string | null;
  sender_address?: string | null;
  /** Si las respuestas de las empresas se reenvían al correo de siempre. */
  forward_replies?: boolean;
  /** A dónde se reenvían: el correo real del candidato. */
  reply_to?: string | null;
  has_cv: boolean;
  has_preferences: boolean;
  /**
   * Si el candidato aparece en la bolsa de talento. El default del producto es
   * ADENTRO, así que sólo un `false` explícito apaga el interruptor y un campo
   * ausente NO es una negativa.
   *
   * Ojo con el desfase: el servidor aplana esto con `=== true`, así que una
   * cuenta anterior al cambio llega en `false` de verdad hasta que corra el
   * backfill, y desde acá es indistinguible de alguien que se salió. Eso se
   * arregla corriendo el backfill, no leyendo distinto este campo.
   */
  talent_pool_opt_in?: boolean;
  /**
   * La nota de la hoja de vida, sólo lo GUARDADO — este endpoint nunca llama al
   * modelo. `null` significa "todavía no se calculó" y jamás cero: quien quiera
   * el número para un CV viejo pide `GET /api/cv/score`, que lo calcula y lo
   * persiste. Normaliza con `normalizeScoreDetail` de `lib/cvScore`.
   */
  cv_score?: number | null;
  cv_score_detail?: unknown;
  /** Situación migratoria H-2 contestada, o null. Ver `lib/immigration`. */
  h2_status?: H2Status | null;
  /**
   * Si el alta le pide «Tu situación migratoria»: postula a EE. UU. y no ha
   * contestado. AUSENTE (un servidor anterior a la pregunta) cuenta como false:
   * una respuesta vieja no puede mandar a nadie a un paso que no podría guardar.
   */
  needs_h2_status?: boolean;
  /** Papeles de la UE contestados, o null. Ver `lib/immigration`. */
  eu_work_authorization?: EuWorkAuthorization | null;
  /**
   * Si el paso le pregunta también «¿Puedes trabajar legalmente en la Unión
   * Europea?»: eligió Europa y no ha contestado. Ausente (servidor viejo) = false.
   */
  needs_eu_authorization?: boolean;
  /** Si el panel ofrece «Europa» entre los países (EURES_RELEASE en el servidor). */
  eures_available?: boolean;
  /**
   * `JOB_APPLY_FREE_TIER=on`: se postula a mano sin plan, y la cinta del plan
   * es neutra y no abre el aviso sola. Ausente (servidor que no lo manda) = false.
   */
  free_tier?: boolean;
  /**
   * Falta la elección del perfil público y la aceptación de los términos
   * vigentes (cuentas anteriores al consentimiento). Siempre false con la
   * bandera apagada; ausente = false.
   */
  needs_consent?: boolean;
  /** La elección vigente del perfil público, o null si nunca decidió (= privado). */
  public_profile?: 'public' | 'private' | null;
  /** La hoja pública (jobs.tbit.app/perfiles/<código>); null si es privado o las hojas están apagadas. */
  public_profile_url?: string | null;
  status: string;
}

/**
 * Si el alta todavía tiene que pedir la situación migratoria. Nunca en «Ver
 * como»: la vista es de solo lectura, el PATCH se cortaría con 403 y el
 * operador quedaría atrapado en un paso que no puede pasar.
 */
export function needsImmigrationStep(status: OnboardingStatus | null): boolean {
  return (status?.needs_h2_status === true || status?.needs_eu_authorization === true) && !currentViewAs();
}

/**
 * Se puede postular desde el Gmail propio o desde la dirección que administra
 * TBit. `has_email_channel` cubre las dos; el respaldo en `has_gmail` mantiene
 * viva una respuesta vieja del servidor.
 */
export function hasEmailChannel(status: OnboardingStatus): boolean {
  return status.has_email_channel ?? status.has_gmail;
}

/**
 * Si el alta puede OFRECER el camino de Gmail. Ausente se lee como "no": el
 * default del servidor es 'off' y una respuesta vieja no puede reabrir una
 * puerta que se cerró a propósito.
 */
export function isGmailPathOffered(status: OnboardingStatus | null): boolean {
  return status?.gmail_signup_enabled === true;
}

/** Si se puede activar el canal gestionado ahora. Ausente se lee como "sí". */
export function isManagedChannelAvailable(status: OnboardingStatus | null): boolean {
  return status?.domain_channel_available ?? true;
}

/**
 * En qué punto del alta está esta persona. Es lo que el shell mira para saber
 * si dibuja la navegación completa o sólo el hilo del alta.
 *
 *   'sender'    todavía no tiene por dónde salir un correo
 *   'cv'          tiene canal, le falta la hoja de vida
 *   'immigration' tiene hoja de vida, falta «Tu situación migratoria» (quien
 *                 postula a EE. UU. y no contestó lo de la H-2, o quien eligió
 *                 Europa y no dijo si tiene papeles de la UE; ver
 *                 `needsImmigrationStep`). Quien
 *                 terminó el alta antes de esta pregunta vuelve UNA vez a
 *                 contestarla; el envío no depende de ella.
 *   'complete'    puede postular
 */
export type OnboardingStage = 'sender' | 'cv' | 'immigration' | 'complete';

export function onboardingStage(status: OnboardingStatus | null): OnboardingStage {
  if (!status || !hasEmailChannel(status)) return 'sender';
  if (!status.has_cv) return 'cv';
  if (needsImmigrationStep(status)) return 'immigration';
  return 'complete';
}

/**
 * La condición completa para mandar a alguien al panel. Incluye el estado
 * ACTIVE de la membresía, que las otras dos señales no cubren: se puede tener
 * canal y hoja de vida con la cuenta suspendida.
 */
export function isOnboardingComplete(status: OnboardingStatus | null): boolean {
  return (
    !!status &&
    hasEmailChannel(status) &&
    status.has_cv &&
    !needsImmigrationStep(status) &&
    status.status === 'ACTIVE'
  );
}
